import React from 'react'
import Calendar from 'react-calendar'
import {Button, Icon} from '@thryvlabs/maverick'
import {Spinner} from '../custom/loader-spinner'
import {
  CallHeading,
  CurrentAppointmentBanner,
  ScheduledCallNotice,
} from './scheduled-call-notice'
import 'react-calendar/dist/Calendar.css'
import '../../styles/partnerScheduler.css'

const SLOT_DURATION_LABEL = '30 minutes'

// Small line icon for the column labels (calendar / clock), as in ooe-app.
const LabelIcon = ({children}) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
)

// Presentational only — every rule and API call lives in usePartnerScheduling.
export const SchedulerAppointmentView = ({scheduler}) => {
  const {
    scheduleOptions,
    activeCall,
    selectCall,
    showLanguageToggle,
    isSpanish,
    changeLanguage,
    dateWindow,
    hasDates,
    selectedDate,
    selectDate,
    slots,
    selectedSlot,
    selectSlot,
    isLoadingSlots,
    timeZoneLabel,
    bookedDateTime,
    isRescheduling,
    startRescheduling,
    stopRescheduling,
    isConfirming,
    canConfirm,
    confirmScheduling,
    canSkip,
    skipScheduling,
  } = scheduler

  const showBooked = !!bookedDateTime && !isRescheduling

  const renderSlots = () => {
    if (!selectedDate) {
      return (
        <div className="ps-slots-empty">
          Select a date to see available time slots.
        </div>
      )
    }
    if (isLoadingSlots) {
      return <div className="ps-slots-loading">Checking Availability</div>
    }
    if (slots.length === 0) {
      return (
        <div className="ps-slots-empty">
          No times available for this date. Please try another date.
        </div>
      )
    }
    return slots.map((slot, index) => (
      <button
        key={slot.startDateTime || slot.startTime || index}
        type="button"
        className={`ps-slot${
          selectedSlot?.startDateTime === slot.startDateTime
            ? ' ps-slot--selected'
            : ''
        }`}
        onClick={() => selectSlot(slot)}
      >
        {slot.displayTime}
      </button>
    ))
  }

  return (
    <div className="ps">
      {/* With several calls the tabs below carry the names; with one, the
          heading does (same as the success page). */}
      <div
        className={`ps-head${
          scheduleOptions.length > 1 ? ' ps-head--tabs' : ''
        }`}
      >
        {scheduleOptions.length === 1 && activeCall && (
          <CallHeading>{activeCall.label}</CallHeading>
        )}
        {scheduleOptions.length > 1 && (
          <div className="ps-tabs">
            {scheduleOptions.map(option => (
              <button
                key={option.name}
                type="button"
                className={`ps-tab${
                  option.name === activeCall?.name ? ' ps-tab--active' : ''
                }`}
                onClick={() => selectCall(option.label)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        <div className="ps-meta">
          <span className="ps-meta-item">
            <Icon
              type="regular"
              variant="calendarClock"
              color="#111827"
              height="20"
              width="20"
            />
            {SLOT_DURATION_LABEL}
          </span>
          <span className="ps-meta-item">
            <Icon
              type="regular"
              variant="globe"
              color="#111827"
              height="20"
              width="20"
            />
            {timeZoneLabel}
          </span>
        </div>
      </div>

      {showBooked ? (
        <ScheduledCallNotice
          label={activeCall.label}
          dateTime={bookedDateTime}
          onReset={startRescheduling}
        />
      ) : (
        <>
          {isRescheduling && bookedDateTime && (
            <CurrentAppointmentBanner
              label={activeCall.label}
              dateTime={bookedDateTime}
              onKeep={stopRescheduling}
            />
          )}
          {showLanguageToggle && (
            <div className="ps-language">
              <span className="ps-language-label">
                Would you like to schedule this call in Spanish?
              </span>
              <div className="ps-toggle">
                <button
                  type="button"
                  className={`ps-toggle-btn${
                    isSpanish ? ' ps-toggle-btn--active' : ''
                  }`}
                  disabled={isLoadingSlots}
                  onClick={() => changeLanguage(true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`ps-toggle-btn${
                    !isSpanish ? ' ps-toggle-btn--active' : ''
                  }`}
                  disabled={isLoadingSlots}
                  onClick={() => changeLanguage(false)}
                >
                  No
                </button>
              </div>
            </div>
          )}

          <div className="ps-columns">
            <div className="ps-column">
              <div className="ps-column-label">
                <LabelIcon>
                  <rect x="3" y="4" width="18" height="17" rx="2" />
                  <path d="M3 9h18M8 2v4M16 2v4" />
                </LabelIcon>
                Select a Date
              </div>
              {hasDates ? (
                <Calendar
                  value={selectedDate}
                  onChange={value =>
                    selectDate(Array.isArray(value) ? value[0] : value)
                  }
                  minDate={dateWindow.min}
                  maxDate={dateWindow.max}
                  locale="en-US"
                />
              ) : (
                <div className="ps-slots-empty">
                  No dates are available to book right now.
                </div>
              )}
            </div>

            <div className="ps-column">
              <div className="ps-column-label">
                <LabelIcon>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </LabelIcon>
                Available Time Slot
              </div>
              <div className="ps-slots">{renderSlots()}</div>
            </div>
          </div>
        </>
      )}

      <div className="ps-actions">
        {canSkip && !showBooked && (
          <Button
            variant="secondary"
            className="ad-secondary"
            onClick={skipScheduling}
            disabled={isConfirming}
          >
            Skip for Now
          </Button>
        )}
        {!showBooked && (
          <Button
            variant="primary"
            className="ad-continue ad-schedule-action ps-schedule-btn"
            onClick={confirmScheduling}
            disabled={!canConfirm}
          >
            <Icon
              type="regular"
              variant="calendarClock"
              color="white"
              height="16"
              width="16"
            />
            Schedule Call
          </Button>
        )}
      </div>

      {isConfirming && (
        <div className="ps-overlay">
          <Spinner />
        </div>
      )}
    </div>
  )
}
